export type AttackType = 'phishing' | 'smishing' | 'vishing';

export interface QuizOption {
  id: 'safe' | 'suspicious';
  label: string;
}

export interface QuizQuestion {
  id: string;
  type: AttackType;
  channel: string;
  scenario: string;
  sender?: string;
  correctAnswer: 'safe' | 'suspicious';
  explanation: string;
}

export const QUIZ_OPTIONS: QuizOption[] = [
  { id: 'safe', label: 'Looks safe' },
  { id: 'suspicious', label: 'Looks suspicious' },
];

export const QUESTIONS: QuizQuestion[] = [
  {
    id: 'phish-1',
    type: 'phishing',
    channel: 'Email',
    sender: 'HMRC Refunds <refunds@hmrc-gov-uk.support>',
    scenario:
      'You have an outstanding tax refund of £284.50. Click here within 24 hours to claim before the funds are returned to HM Treasury: http://hmrc-gov-uk.support/refund/claim',
    correctAnswer: 'suspicious',
    explanation:
      'HMRC never asks for refund claims via email and never uses domains like "hmrc-gov-uk.support". Urgency + spoofed lookalike domain are classic phishing tells.',
  },
  {
    id: 'phish-2',
    type: 'phishing',
    channel: 'Email',
    sender: 'IT Support <it@yourcompany.co.uk>',
    scenario:
      'Reminder: your Microsoft 365 password expires today. Please use the self-service portal at portal.office.com to update it before end of day.',
    correctAnswer: 'safe',
    explanation:
      'Generic expiry reminder pointing to the legitimate Microsoft domain (portal.office.com), no urgent link or attachment, sent from your own IT domain. Still verify with IT if unsure — but nothing here flags as malicious.',
  },
  {
    id: 'smish-3',
    type: 'smishing',
    channel: 'SMS',
    sender: 'HSBC Alerts',
    scenario:
      'HSBC: A payment of £849.00 to "AMZN MKT" was attempted from your account. If this was NOT you, verify here: hsbc-secure.verify-id.com',
    correctAnswer: 'suspicious',
    explanation:
      'Banks send fraud alerts but never link to a non-bank domain. "verify-id.com" is the attacker\'s site. Real HSBC alerts ask you to call the number on the back of your card.',
  },
  {
    id: 'phish-3',
    type: 'phishing',
    channel: 'Email',
    sender: 'Sarah Bell, CEO <s.bell.driscop@gmail.com>',
    scenario:
      'Hi, I\'m in a meeting and can\'t talk. I need you to buy £400 in Amazon gift cards for a client thank-you. Send me the codes ASAP and I\'ll expense it tomorrow.',
    correctAnswer: 'suspicious',
    explanation:
      'Classic CEO-fraud / Business Email Compromise: spoofed personal Gmail, urgency, secrecy ("can\'t talk"), and gift-card payment — a payment rail no legitimate business uses for expenses.',
  },
  {
    id: 'smish-1',
    type: 'smishing',
    channel: 'SMS',
    sender: '+44 7700 900421',
    scenario:
      'Royal Mail: Your parcel is held due to an unpaid £1.99 customs fee. Pay now to release: https://royalmail-redelivery.parcel-fee.uk',
    correctAnswer: 'suspicious',
    explanation:
      'Royal Mail never asks for customs payment via random SMS links. Lookalike domain, tiny fee designed to bypass scepticism, link shortener / unfamiliar TLD — textbook smishing.',
    },
  {
    id: 'smish-2',
    type: 'smishing',
    channel: 'SMS',
    sender: 'NHS-NoReply',
    scenario:
      'NHS: Your appointment at Manchester Royal Infirmary is confirmed for Tue 26 May, 10:15. Reply CANCEL to rebook. Do not reply with personal info.',
    correctAnswer: 'safe',
    explanation:
      'Confirms an appointment you already booked, no link to click, no payment, no request for personal info. Aligns with how the NHS actually communicates.',
  },
  {
    id: 'vish-1',
    type: 'vishing',
    channel: 'Phone call',
    sender: 'Caller ID: "Microsoft Support"',
    scenario:
      '"We\'ve detected malware on your work laptop sending data to a server in Russia. I need you to install our remote support tool, AnyDesk, so I can clean it before it spreads to the rest of your network."',
    correctAnswer: 'suspicious',
    explanation:
      'Microsoft (and no legitimate vendor) cold-calls about malware. Remote-access tools + manufactured urgency = vishing. Hang up and call IT directly using a number you already have.',
  },
  {
    id: 'vish-2',
    type: 'vishing',
    channel: 'Phone call',
    sender: 'Caller ID: your bank\'s published fraud line',
    scenario:
      '"This is Lloyds fraud team. We\'ve blocked a suspicious transaction. For your security, please end this call and dial the number on the back of your card to confirm the case reference 4421-A."',
    correctAnswer: 'safe',
    explanation:
      'A real fraud team will tell you to hang up and call the published number — the opposite of what a scammer does. No pressure, no remote tools, no credentials requested.',
  },
  {
    id: 'vish-3',
    type: 'vishing',
    channel: 'Phone call',
    sender: 'Caller ID: withheld',
    scenario:
      '"Hi, this is Tom from your IT provider. We\'re rolling out an MFA update and I just need you to read me the 6-digit code your Microsoft Authenticator app is showing right now so I can pair the new device."',
    correctAnswer: 'suspicious',
    explanation:
      'No one — IT, your bank, or any vendor — should ever ask you to read out an MFA code. That code is the attacker\'s key to bypass your second factor. Hang up.',
  },
];

export type Tier = 'low' | 'moderate' | 'strong';

export interface TierInfo {
  tier: Tier;
  label: string;
  headline: string;
  summary: string;
  accentClass: string;
}

export function scoreToTier(correct: number, total: number): TierInfo {
  const pct = total === 0 ? 0 : correct / total;
  if (pct >= 0.8) {
    return {
      tier: 'strong',
      label: 'Strong risk awareness',
      headline: 'You spot social engineering reliably.',
      summary:
        "You're catching the lookalike domains, manufactured urgency, and unusual payment requests that fool most people. Stay sharp, attackers keep evolving, and a tired Friday afternoon is all it takes for one to land.",
      accentClass: 'text-green-400',
    };
  }
  if (pct >= 0.5) {
    return {
      tier: 'moderate',
      label: 'Moderate risk awareness',
      headline: 'Good instincts, with blind spots worth closing.',
      summary:
        'You caught most of the obvious attacks but missed some of the polished ones. Modern phishing, smishing and vishing increasingly mimic real workflows, a layered detection tool catches what trained eyes miss.',
      accentClass: 'text-yellow-400',
    };
  }
  return {
    tier: 'low',
    label: 'Low risk awareness',
    headline: 'You\'re at elevated risk of falling for a social engineering attack.',
    summary:
      "That's not a judgement, these scams are designed to slip past busy people. But it does mean you (or your team) would benefit from automated detection that flags suspicious messages before they reach you.",
    accentClass: 'text-red-400',
  };
}
